<script setup lang="ts">
type FaqItem = {
  question?: string;
  title?: string;
  answer?: string;
  text1?: string;
  open?: boolean;
};

const props = defineProps<{
  parentPageTitle?: string | null;
  faqs?: FaqItem[] | null;
}>();

/** Curated map-intent FAQs when CMS FAQ page has no matching items. */
const FALLBACK_BY_TREK: Record<string, FaqItem[]> = {
  "tour du mont blanc": [
    {
      question: "What is the difference between the Full, Westerly and Easterly routes?",
      answer:
        "<p>The Full Route is the classic Tour du Mont Blanc circuit around Mont Blanc. The Westerly and Easterly maps show the same circuit emphasised by direction of travel — useful when comparing anti-clockwise (westerly start emphasis) and clockwise (easterly) itineraries.</p>",
    },
    {
      question: "Should I trek clockwise or anti-clockwise?",
      answer:
        "<p>Both directions are popular. Anti-clockwise is the traditional circuit many of our packages follow. Your package itinerary confirms direction, overnight stops, and daily distances — check dates and availability below, then enquire if you are unsure which suits you.</p>",
    },
    {
      question: "When is the best time to trek the Tour du Mont Blanc?",
      answer:
        "<p>The main season runs from mid-June to mid-September, when mountain huts and high passes are typically open. July and August are busiest; June and September are often quieter with cooler temperatures.</p>",
    },
    {
      question: "How hard is the Tour du Mont Blanc?",
      answer:
        "<p>It is a multi-day alpine trek with sustained daily ascent and descent. Most of our TMB packages are graded for fit walkers with mountain experience. See Key facts above and each package for duration, distance, and grade details.</p>",
    },
  ],
};

function normalize(value: string | null | undefined) {
  return String(value ?? "")
    .toLowerCase()
    .trim()
    .replace(/['']/g, "");
}

const MAP_INTENT =
  /\b(map|route|clockwise|anti-?clockwise|west|east|direction|season|when|month|grade|difficult|hard|fitness|distance|elevation|circuit)\b/i;

const filteredCmsFaqs = computed(() => {
  const list = Array.isArray(props.faqs) ? props.faqs : [];
  const matched = list.filter((item) => {
    const q = `${item.question ?? ""} ${item.title ?? ""}`;
    const a = `${item.answer ?? ""} ${item.text1 ?? ""}`;
    return MAP_INTENT.test(q) || MAP_INTENT.test(a);
  });
  return matched.slice(0, 6).map((item) => ({ ...item, open: false }));
});

const accordionFaqs = computed(() => {
  if (filteredCmsFaqs.value.length) return filteredCmsFaqs.value;
  const key = normalize(props.parentPageTitle);
  const fallback =
    FALLBACK_BY_TREK[key] ??
    Object.entries(FALLBACK_BY_TREK).find(([name]) =>
      key.includes(name),
    )?.[1];
  return (fallback ?? []).map((item) => ({ ...item, open: false }));
});
</script>

<template>
  <section
    v-if="accordionFaqs.length"
    class="map-page-faqs"
    aria-labelledby="map-page-faqs-heading"
  >
    <header>
      <h2 id="map-page-faqs-heading">Map &amp; Route FAQs</h2>
    </header>
    <AppAccordion :acc-data="accordionFaqs" />
  </section>
</template>
